import { useEffect, useMemo, useState, useRef } from "react";
import Input from "../../common/Input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { searchCustomerByNumber, savePatientDetails } from "../../../redux/Thunk/AdminAccount.js/AccountThunk";
import debounce from "lodash/debounce";
import { memo } from "react";
import toast from "react-hot-toast";
import CheckedBg from "../../common/icons/CheckedBg";
import Close from "../../common/icons/Close";
import Timing from "../../common/icons/Timing";
import pincodeData from "../../../utils/pincodeCityDirectory.json";
import HealthMapReportEditor from "../HealthMap/HealthMapReportEditor";
import "../HealthMap/HealthMap.css";

const PatientOrderDetail = ({
  handleChange,
  values,
  setFieldValue,
  selectedDoctor,
  setSelectedDoctor,
  touched,
  errors,
  setFieldTouched,
  handleBlur,
  readOnlyPhone,
  onQuickOrder,
}) => {
  const doctors = useSelector(
    (state) => state?.doctor?.doctorAssociatedWithStore
  );
  const role = useSelector((state) => state?.auth?.role);
  const orderById = useSelector((state) => state?.order?.orderById);
  const isPinB = useSelector((state) => state.auth?.user?.isPinB);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // New states for Pincode/City flow
  const [cityMode, setCityMode] = useState(false);
  const [ageMode, setAgeMode] = useState(false);
  const [pincodeSuggestions, setPincodeSuggestions] = useState([]);
  const [showPincodeSuggestions, setShowPincodeSuggestions] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showReportOverlay, setShowReportOverlay] = useState(false);

  const pincodeRef = useRef(null);
  const cityRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const debouncedHandlePhoneNumberChange = useMemo(
    () =>
      debounce(async (phoneNumber) => {
        if (phoneNumber?.length >= 3 && phoneNumber?.length <= 10) {
          // Fetch matching phone numbers
          try {
            const response = await dispatch(
              searchCustomerByNumber(phoneNumber)
            ).unwrap();
            const data = response;

            if (data && data?.length > 0) {
              setSuggestions(data); // Set matching phone numbers
              setShowSuggestions(true); // Show the dropdown
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }
          } catch (error) {
            console.error("Error fetching phone numbers", error);
          }
        } else {
          setSuggestions([]);
          setShowSuggestions(false); // Hide suggestions if input is cleared or too short
        }
      }, 300),
    [dispatch]
  );

  const [isExistingPatient, setIsExistingPatient] = useState(false);
  const [initialFetchedValues, setInitialFetchedValues] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const currentDoctor = useSelector((state) => state?.doctor?.doctorDetails?.doctor);

  useEffect(() => {
    return () => {
      debouncedHandlePhoneNumberChange.cancel();
    };
  }, [debouncedHandlePhoneNumberChange]);

  useEffect(() => {
    if (Object.keys(orderById)?.length > 0) {
      setSelectedDoctor(orderById);
    }
  }, [orderById, setSelectedDoctor]);

  // Auto-swap fields based on populated data (useful for AI extractions)
  useEffect(() => {
    if (values?.patient?.age && !values?.patient?.dateofbirth) {
      setAgeMode(true);
    } else if (values?.patient?.dateofbirth && !values?.patient?.age) {
      setAgeMode(false);
    }
    
    if (values?.patient?.city && !values?.patient?.pincode) {
      setCityMode(true);
    } else if (values?.patient?.pincode && !values?.patient?.city) {
      setCityMode(false);
    } else if (values?.patient?.city) {
      setCityMode(true);
    }
  }, [values?.patient?.age, values?.patient?.dateofbirth, values?.patient?.city, values?.patient?.pincode]);

  // Handle closing dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pincodeRef.current && !pincodeRef.current.contains(event.target)) {
        setShowPincodeSuggestions(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Completion Logic
  const isNameFilled = values?.patient?.name?.trim()?.length > 0;
  const isSurnameFilled = values?.patient?.surname?.trim()?.length > 0;
  const isPhoneFilled = values?.patient?.phoneNumber?.length >= 10;
  const isGenderFilled = values?.patient?.gender?.length > 0;
  const isPincodeFilled = values?.patient?.pincode?.length === 6;
  const isCityFilled = values?.patient?.city?.trim()?.length > 0;
  const isDobFilled = !!values?.patient?.dateofbirth;
  const isAgeFilled = String(values?.patient?.age)?.trim()?.length > 0;

  const isLocationFilled = cityMode ? isCityFilled : isPincodeFilled;
  const isAgeMetricFilled = ageMode ? isAgeFilled : isDobFilled;

  const isAllFilled = isPhoneFilled && isNameFilled && isSurnameFilled && isGenderFilled && isLocationFilled && isAgeMetricFilled;

  const getPendingFields = () => {
    const pending = [];
    if (!isPhoneFilled) pending.push("Contact");
    if (!isNameFilled || !isSurnameFilled) pending.push("Name");
    if (!isGenderFilled) pending.push("Gender");
    if (!isLocationFilled) pending.push(cityMode ? "City" : "Pincode");
    if (!isAgeMetricFilled) pending.push(ageMode ? "Age" : "DoB");
    return pending;
  };

  const getPendingStyle = (isFilled, currentError) => {
    if (currentError) return ""; // Let error style take priority
    if (!isFilled) return "ring-1 ring-primary-300 border-primary-300 bg-white";
    return "";
  };

  // Sync modes with loaded data (initial mount for pre-fills)
  useEffect(() => {
    if (values?.patient?.city && !values?.patient?.pincode) {
      setCityMode(true);
    }
    if (values?.patient?.age && !values?.patient?.dateofbirth) {
      setAgeMode(true);
    }
    // If PID is present from external load (Open/Edit tabs), mark as existing
    if (values?.patient?.PID) {
      setIsExistingPatient(true);
      // Also set initialFetchedValues so "hasChanges" detection works for Update button
      if (!initialFetchedValues) {
        setInitialFetchedValues({ ...values.patient });
      }
    } else {
      setIsExistingPatient(false);
      setInitialFetchedValues(null);
    }
  }, [values?.patient?.PID, values?.patient, initialFetchedValues]); // Run when PID changes or on mount

  const handlePhoneNumberChange = async (e) => {
    const phoneNumber = e.target.value;
    setFieldValue("patient.phoneNumber", phoneNumber);

    if (phoneNumber?.length <= 10) {
      debouncedHandlePhoneNumberChange(phoneNumber);
    }
    // If phone number changes and doesn't match fetched person, reset existing status
    if (isExistingPatient && initialFetchedValues && phoneNumber !== initialFetchedValues.phoneNumber) {
      setIsExistingPatient(false);
      setInitialFetchedValues(null);
      setFieldValue("patient.PID", null);
    }
  };

  const nameInputRef = useRef(null);
  const surnameInputRef = useRef(null);

  const handleNameKeyDown = (e) => {
    if (e.key === " " && values?.patient?.name?.trim().length > 0) {
      e.preventDefault();
      surnameInputRef.current?.focus();
    }
  };
  const handleSelectSuggestion = (e, suggestion) => {
    e.preventDefault();
    setFieldValue("patient.phoneNumber", suggestion.phoneNumber);
    setFieldValue("patient.name", suggestion.name || "");
    setFieldValue("patient.surname", suggestion.surname || "");
    setFieldValue("patient.dateofbirth", suggestion.dateofbirth || "");
    setFieldValue("patient.age", suggestion.age || "");
    setFieldValue("patient.gender", suggestion.gender || "");
    setFieldValue("patient.PID", suggestion.PID || "");

    setIsExistingPatient(true);
    setInitialFetchedValues({
      phoneNumber: suggestion.phoneNumber || "",
      name: suggestion.name || "",
      surname: suggestion.surname || "",
      gender: suggestion.gender || "",
      dateofbirth: suggestion.dateofbirth || "",
      age: suggestion.age || "",
      pincode: suggestion.pincode || suggestion.patientAddress?.pincode || "",
      city: suggestion.city || suggestion.patientAddress?.city || "",
    });

    // Autofill pincode and city with fallback to patientAddress
    const pincode = suggestion.pincode || suggestion.patientAddress?.pincode || "";
    const city = suggestion.city || suggestion.patientAddress?.city || "";
    setFieldValue("patient.pincode", pincode);
    setFieldValue("patient.city", city);

    // Toggle swappable modes
    if (city && !pincode) {
      setCityMode(true);
    } else {
      setCityMode(false);
    }

    if (suggestion.age && !suggestion.dateofbirth) {
      setAgeMode(true);
    } else {
      setAgeMode(false);
    }

    if (suggestion.patientAddress) {
      setFieldValue("delivery.address.addressType", "");
      setFieldValue("delivery.address.areaRoad", suggestion.patientAddress?.areaRoad);
      setFieldValue("delivery.address.city", suggestion.patientAddress?.city);
      setFieldValue("delivery.address.pincode", suggestion.patientAddress?.pincode);
      setFieldValue("delivery.address.premisesName", suggestion.patientAddress?.premisesName);
      setFieldValue("delivery.address.premisesNoFloor", suggestion.patientAddress?.premisesNoFloor);
      setFieldValue("delivery.address.state", suggestion.patientAddress?.state);
      setFieldValue("delivery.address.landmark", suggestion.patientAddress?.landmark);
      setFieldValue("delivery.address.phoneNumber2", suggestion.patientAddress?.phoneNumber2);
    }
    setFieldTouched("patient.phoneNumber", false);
    setShowSuggestions(false);
  };

  const handlePincodeChange = (e) => {
    const query = e.target.value;
    setFieldValue("patient.pincode", query);
    setFieldValue("patient.city", "");
    setFieldValue("delivery.address.pincode", query);

    if (query.trim().length >= 3) {
      const matched = pincodeData.filter(d => String(d.pincode).startsWith(query)).slice(0, 100);
      setPincodeSuggestions(matched);
      setShowPincodeSuggestions(true);
    } else {
      setShowPincodeSuggestions(false);
    }
  };

  const handleSelectPincode = (item) => {
    setFieldValue("patient.pincode", item.pincode);
    setFieldValue("delivery.address.pincode", item.pincode);
    setFieldValue("patient.city", item.city || "");
    setFieldValue("delivery.address.city", item.city || "");
    setFieldValue("delivery.address.state", item.state || "");
    setShowPincodeSuggestions(false);
  };

  const handleCityChange = (e) => {
    const query = e.target.value;
    setFieldValue("patient.city", query);
    setFieldValue("patient.pincode", "");
    setFieldValue("delivery.address.city", query);

    if (query.trim().length >= 3) {
      const matched = [];
      const seen = new Set();
      for (const d of pincodeData) {
        if (d.city && d.city.toLowerCase().includes(query.toLowerCase())) {
          const key = `${d.city}-${d.state}`;
          if (!seen.has(key)) {
            seen.add(key);
            matched.push(d);
            if (matched.length >= 20) break;
          }
        }
      }
      setCitySuggestions(matched);
      setShowCitySuggestions(true);
    } else {
      setShowCitySuggestions(false);
    }
  };

  const handleSelectCity = (item) => {
    setFieldValue("patient.city", item.city);
    setFieldValue("delivery.address.city", item.city);
    setFieldValue("delivery.address.state", item.state || "");
    setFieldValue("patient.pincode", "");
    setFieldValue("delivery.address.pincode", "");
    setShowCitySuggestions(false);
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectDoctor = (e, doctor) => {
    e.preventDefault();
    setFieldValue("DID", doctor?.DID);
    setSelectedDoctor(doctor);
    setIsOpen(false);
  };

  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const genderRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pincodeRef.current && !pincodeRef.current.contains(event.target)) {
        setShowPincodeSuggestions(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
      if (genderRef.current && !genderRef.current.contains(event.target)) {
        setShowGenderDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return (
    <div className="w-full border border-primary-100 bg-white p-4 rounded-[12px] min-h-[108px]">
      <div className="flex flex-col xl:flex-row gap-x-4">
        {role === "store" ? (
          <div className="w-full xl:max-w-[300px] flex-shrink-0 relative max-lg:mb-2">
            <h4 className="text-[16px] font-medium mb-1 text-secondry-600">
              Doctor
            </h4>
            <div
              onClick={handleToggleDropdown}
              className="p-2 bg-gray-100 text-gray-700 border h-[56px] flex items-center justify-between border-gray-300 rounded-md cursor-pointer"
            >
              <div>
                {(Object.keys(selectedDoctor)?.length > 0 ? (
                  <div className="flex items-center gap-3">
                    <div className="w-[20px] h-[20px]">
                      {selectedDoctor?.currentDoctorStatus &&
                        (selectedDoctor?.currentDoctorStatus === "ACTIVE" ? (
                          <CheckedBg fill="#00C732" />
                        ) : selectedDoctor?.currentDoctorStatus === "CLOSED" ? (
                          <Close />
                        ) : selectedDoctor?.currentDoctorStatus === "AWAY" ? (
                          <Timing />
                        ) : null)}
                    </div>
                    <div className="w-[calc(100%_-_30px)]">
                      <div className="font-medium text-gray-800">
                        Dr. {selectedDoctor?.personalInfo?.name ||
                          selectedDoctor?.doctor?.personalInfo?.name ||
                          ""}{" "}
                        {selectedDoctor?.personalInfo?.surname ||
                          selectedDoctor?.doctor?.personalInfo?.surname ||
                          ""}
                      </div>
                      <div className="text-sm text-gray-500">
                        #{selectedDoctor?.DID} •{" "}
                        {selectedDoctor?.personalInfo?.clinicName ||
                          selectedDoctor?.doctor?.personalInfo?.clinicName ||
                          ""}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>Search or Select Doctor </p>
                )) || "Search or Select Doctor"}
              </div>
              <span>
                <img src="icons/arrow-down.svg" className="h-[30px] w-[30px]" alt="arrow down" />
              </span>
            </div>

            {isOpen ? (
              <div className="absolute z-[999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                <ul>
                  {doctors[0]?.doctors?.length > 0 ? (
                    doctors[0]?.doctors?.map((doctor) => (
                      <li
                        key={doctor?.DID}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSelectDoctor(e, doctor);
                        }}
                        className="px-4 py-2.5 cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-[20px] h-[20px]">
                            {doctor.currentDoctorStatus &&
                              (doctor?.currentDoctorStatus === "ACTIVE" ? (
                                <CheckedBg fill="#00C732" />
                              ) : doctor?.currentDoctorStatus === "CLOSED" ? (
                                <Close />
                              ) : doctor?.currentDoctorStatus === "AWAY" ? (
                                <Timing />
                              ) : null)}
                          </div>
                          <div className="w-[calc(100%_-_30px)]">
                            <div className="font-medium text-gray-800">
                              Dr. {doctor?.personalInfo?.name} {doctor?.personalInfo?.surname}
                            </div>
                            <div className="text-sm text-gray-500">
                              #{doctor?.DID} • {doctor?.personalInfo?.clinicName}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="px-4 py-2.5">No Doctor Linked!!</li>
                  )}
                </ul>
              </div>
            ) : (
              ""
            )}
          </div>
        ) : null}
        <div
          className={`flex-1 flex flex-col gap-y-3 ${role === "store" ? "xl:col-span-8" : "xl:col-span-12"
            }`}
        >
          {/* Mobile Title Tile */}
          <div className="flex sm:hidden items-center gap-2 px-4 py-2 bg-white border border-primary-100 rounded-[8px] shadow-sm justify-center w-full">
            <svg width="16" height="16" viewBox="0 0 24 24" fill={isAllFilled ? "#00C732" : "#0ea5e9"} xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]">
              {isAllFilled ? (
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#00C732" />
              ) : (
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="#0ea5e9" />
              )}
            </svg>
            <h3 className="text-[15px] font-bold text-secondry-850 tracking-wide uppercase">
              Patient
            </h3>
          </div>

          {/* Consistent Header */}
          <div className="w-full">
            <div className="flex flex-wrap items-center p-1 bg-primary-50 border-[0.5px] border-primary-100 rounded-[8px] w-full shadow-sm relative gap-1">
              <div className="hidden sm:flex p-1">
                <div className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white rounded-[4px]">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={isAllFilled ? "#00C732" : "#0ea5e9"} xmlns="http://www.w3.org/2000/svg" className="md:w-[18px] md:h-[18px]">
                    {isAllFilled ? (
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#00C732" />
                    ) : (
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="#0ea5e9" />
                    )}
                  </svg>
                  <h3 className="text-[13px] md:text-[15px] font-semibold text-secondry-600 whitespace-nowrap uppercase">
                    Patient
                  </h3>
                </div>
              </div>
              <div className="hidden sm:block w-[1px] h-5 bg-primary-100 mx-1"></div>
              <div className={`hidden md:block px-3 text-[12px] font-medium transition-all duration-300 ${isAllFilled ? "text-success-600" : "text-primary-400"}`}>
                {isAllFilled ? (
                  <span className="flex items-center gap-1">Everything looks good!</span>
                ) : (
                  <span>Enter patient details {getPendingFields().length > 0 && <span className="opacity-70">(Pending: {getPendingFields().join(", ")})</span>}</span>
                )}
              </div>
              <div className="flex-1 md:flex-none flex items-center justify-end gap-1.5 p-1 ml-auto">
                {(() => {
                  const hasChanges = isExistingPatient && initialFetchedValues && (
                    values.patient.name !== initialFetchedValues.name ||
                    values.patient.surname !== initialFetchedValues.surname ||
                    values.patient.gender !== initialFetchedValues.gender ||
                    values.patient.phoneNumber !== initialFetchedValues.phoneNumber ||
                    values.patient.pincode !== initialFetchedValues.pincode ||
                    values.patient.city !== initialFetchedValues.city ||
                    String(values.patient.age) !== String(initialFetchedValues.age || "") ||
                    values.patient.dateofbirth !== (initialFetchedValues.dateofbirth || "")
                  );

                  const buttonText = isExistingPatient ? (hasChanges ? "Update" : "Save") : "Save";
                  const isDisabled = !isAllFilled || (isExistingPatient && !hasChanges) || isSaving;

                  return (
                    <div className="w-full sm:w-auto flex items-center gap-2 sm:gap-1.5 overflow-x-auto no-scrollbar min-w-0 flex-1 justify-end">
                      <button
                        type="button"
                        disabled={isDisabled}
                        onClick={async () => {
                          setIsSaving(true);
                          try {
                            const payload = {
                              patient: values.patient,
                              DID: selectedDoctor?.DID || currentDoctor?.DID
                            };
                            const response = await dispatch(savePatientDetails(payload)).unwrap();
                            const savedPatient = response.data;

                            toast.success(response.message || "Patient details saved successfully");

                            // Update Formik state with the new/existing PID from server
                            if (savedPatient?.PID) {
                              setFieldValue("patient.PID", savedPatient.PID);
                            }

                            // Update initial values after save to reset hasChanges
                            setIsExistingPatient(true);
                            setInitialFetchedValues({
                              phoneNumber: savedPatient?.phoneNumber || values.patient.phoneNumber,
                              name: savedPatient?.name || values.patient.name,
                              surname: savedPatient?.surname || values.patient.surname,
                              gender: savedPatient?.gender || values.patient.gender,
                              dateofbirth: savedPatient?.dateofbirth || values.patient.dateofbirth,
                              age: savedPatient?.age || values.patient.age,
                              pincode: savedPatient?.pincode || values.patient.pincode,
                              city: savedPatient?.city || values.patient.city,
                            });
                          } catch (err) {
                            toast.error(err.message || "Failed to save details");
                          } finally {
                            setIsSaving(false);
                          }
                        }}
                        className={`flex-1 sm:flex-initial text-center justify-center px-3 md:px-4 py-1.5 rounded-[6px] text-[11px] md:text-[12px] font-semibold transition-all duration-300 whitespace-nowrap ${isDisabled
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                          : "bg-success-50 text-success-700 hover:bg-success-600 hover:text-white border border-success-200 shadow-sm"
                          }`}
                      >
                        {isSaving ? "Saving..." : buttonText}
                      </button>

                      {isPinB && isExistingPatient && values.patient.PID && (
                        <button
                          type="button"
                          onClick={() => {
                            // Store the patient context so HealthMap can pick it up
                            sessionStorage.setItem("healthmap-patient-context", JSON.stringify(values.patient));
                            navigate("/healthmap");
                          }}
                          className="flex-1 sm:flex-initial text-center justify-center px-3 md:px-4 py-1.5 rounded-[6px] text-[11px] md:text-[12px] font-bold transition-all duration-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-200 shadow-sm flex items-center gap-1.5 group whitespace-nowrap"
                          title="Open patient timeline"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform md:w-[14px] md:h-[14px]">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                          </svg>
                          <span className="hidden min-[380px]:inline">HealthMap</span>
                          <span className="min-[380px]:hidden">Map</span>
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Add Report Overlay */}
          {showReportOverlay && (
            <div className="hm-theme-light">
              <div className="hm-overlay" onClick={() => setShowReportOverlay(false)} style={{ zIndex: 10000 }}>
                <div className="hm-overlay-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
                  <button className="hm-overlay-close" onClick={() => setShowReportOverlay(false)}>✕</button>
                  <div className="hm-overlay-title">📁 Add Clinical Report</div>
                  <div className="hm-overlay-body">
                    <HealthMapReportEditor
                      PID={values.patient.PID}
                      isNew={true}
                      onDone={() => setShowReportOverlay(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="w-full">
            <div className="grid grid-cols-12 gap-4">
              {/* Row 1: Contact, Full Name, Gender */}
              <div className="col-span-12 md:col-span-4 relative">
                <Input
                  name="patient.phoneNumber"
                  value={values.patient.phoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder="Enter Contact"
                  type="number"
                  labelName="Contact"
                  labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                  className="border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                  onFocus={() => setFieldTouched("patient.phoneNumber", true)}
                  inputBox={getPendingStyle(isPhoneFilled, touched.patient?.phoneNumber && errors.patient?.phoneNumber)}
                  isError={
                    touched.patient?.phoneNumber &&
                    Boolean(errors.patient?.phoneNumber)
                  }
                  errorMessage={errors.patient?.phoneNumber}
                  readOnly={readOnlyPhone}
                />

                {showSuggestions && (
                  <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-md max-h-40 overflow-y-auto rounded-md mt-1">
                    {suggestions?.map((suggestion) => (
                      <li
                        key={suggestion.phoneNumber}
                        onClick={(e) => handleSelectSuggestion(e, suggestion)}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-[13px]"
                      >
                        <div className="font-medium text-gray-800 flex items-center justify-between">
                          <span>{suggestion?.name} {suggestion?.surname}</span>
                          <span className="text-[11px] font-normal text-primary-400 bg-primary-50 px-1 rounded uppercase">
                            {suggestion?.gender?.charAt(0) || "-"} | {suggestion?.age || (suggestion?.dateofbirth ? (new Date().getFullYear() - new Date(suggestion.dateofbirth).getFullYear()) : "-")}y
                          </span>
                        </div>
                        <div className="text-gray-500 flex items-center justify-between">
                          <span>{suggestion?.phoneNumber}</span>
                          <span className="text-[11px] text-gray-400 italic">{suggestion?.city || suggestion?.patientAddress?.city || ""}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="col-span-12 md:col-span-5">
                <div
                  className={`py-[5px] px-[16px] rounded-[6px] border h-[56px] transition-all duration-500 relative flex flex-col justify-center w-full ${(touched.patient?.name && errors.patient?.name) || (touched.patient?.surname && errors.patient?.surname)
                    ? "border-orange-500 bg-orange-50"
                    : isNameFilled && isSurnameFilled
                      ? "border-primary-50 bg-primary-50 focus-within:border-primary-500"
                      : "ring-1 ring-primary-300 border-primary-300 bg-white focus-within:border-primary-500"
                    }`}
                >
                  <label className="block text-primary-400 text-[13px] leading-[18px]">Full Name</label>
                  <div className="flex items-center h-[24px] overflow-hidden whitespace-nowrap w-full">
                    {/* Width Mirror for Name */}
                    <span
                      id="name-mirror"
                      className="absolute opacity-0 pointer-events-none text-[14px]"
                      style={{ visibility: 'hidden', whiteSpace: 'pre', position: 'absolute', top: 0, left: 0 }}
                    >
                      {values?.patient?.name || "Name"}
                    </span>
                    <input
                      ref={nameInputRef}
                      name="patient.name"
                      value={values?.patient?.name}
                      onChange={handleChange}
                      onKeyDown={handleNameKeyDown}
                      placeholder="Name"
                      style={{
                        width: values?.patient?.name
                          ? `${Math.min(300, Math.max(40, (document.getElementById('name-mirror')?.offsetWidth || 40) + 10))}px`
                          : '45px'
                      }}
                      className="bg-transparent outline-none text-[14px] text-secondry-600 placeholder:text-gray-400 min-w-0 transition-[width] duration-75"
                    />

                    <span className="text-primary-200 px-1">-</span>

                    <input
                      ref={surnameInputRef}
                      name="patient.surname"
                      value={values?.patient?.surname}
                      onChange={handleChange}
                      placeholder="Surname"
                      className="bg-transparent outline-none text-[14px] text-secondry-600 placeholder:text-gray-400 flex-1 min-w-0"
                    />
                  </div>
                  {((touched.patient?.name && errors.patient?.name) || (touched.patient?.surname && errors.patient?.surname)) && (
                    <div className="absolute left-0 -bottom-5 text-orange-800 text-[11px] uppercase font-medium">
                      {errors.patient?.name || errors.patient?.surname}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-12 md:col-span-3 relative" ref={genderRef}>
                <div
                  onClick={() => setShowGenderDropdown(!showGenderDropdown)}
                  className={`py-[5px] px-[16px] rounded-[6px] border h-[56px] transition-all duration-500 cursor-pointer relative flex flex-col justify-center ${touched.patient?.gender && errors.patient?.gender
                    ? "border-orange-500 bg-orange-50"
                    : isGenderFilled
                      ? "border-primary-50 bg-primary-50 focus-within:border-primary-500"
                      : "ring-1 ring-primary-300 border-primary-300 bg-white"
                    }`}
                >
                  <label className="block text-primary-400 text-[13px] leading-[18px]">Gender</label>
                  <div className="flex items-center justify-between">
                    <span className={`text-[14px] ${values.patient.gender ? 'text-secondry-600' : 'text-gray-400'}`}>
                      {values.patient.gender || "Select"}
                    </span>
                    <img src="icons/arrow-down.svg" className={`h-5 w-5 transition-transform duration-300 ${showGenderDropdown ? 'rotate-180' : ''}`} alt="" />
                  </div>
                  {touched.patient?.gender && errors.patient?.gender && (
                    <div className="absolute left-0 -bottom-5 text-orange-800 text-[11px] uppercase font-medium">
                      {errors.patient.gender}
                    </div>
                  )}
                </div>

                {showGenderDropdown && (
                  <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-lg rounded-md mt-1 overflow-hidden transition-all duration-200">
                    {["Male", "Female", "Other"].map((option) => (
                      <li
                        key={option}
                        onClick={() => {
                          setFieldValue("patient.gender", option);
                          setShowGenderDropdown(false);
                        }}
                        className={`px-4 py-2.5 text-[14px] cursor-pointer hover:bg-primary-50 transition-colors ${values.patient.gender === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'}`}
                      >
                        {option}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Row 2: Pincode/City, DoB/Age */}
              <div className="col-span-12 md:col-span-6">
                {!cityMode ? (
                  <div ref={pincodeRef} className="relative w-full">
                    <Input
                      name="patient.pincode"
                      value={values?.patient?.pincode}
                      onChange={handlePincodeChange}
                      placeholder="e.g. 400001"
                      type="number"
                      labelName="Pincode"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isPincodeFilled, touched.patient?.pincode && errors.patient?.pincode)}
                      isError={
                        touched.patient?.pincode && Boolean(errors.patient?.pincode)
                      }
                      errorMessage={errors.patient?.pincode}
                    />
                    <button
                      type="button"
                      onClick={() => setCityMode(true)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1"
                    >
                      Swap to City
                    </button>
                    {showPincodeSuggestions && (
                      <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-md max-h-40 overflow-y-auto rounded-md mt-1">
                        {pincodeSuggestions.map((item, idx) => (
                          <li
                            key={item.pincode + '-' + idx}
                            onClick={() => handleSelectPincode(item)}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0 text-[13px]"
                          >
                            <span className="font-medium text-gray-800">{item.pincode}</span>
                            <span className="text-gray-500 ml-2">- {item.area}, {item.city}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div ref={cityRef} className="relative w-full">
                    <Input
                      name="patient.city"
                      value={values?.patient?.city}
                      onChange={handleCityChange}
                      placeholder="Enter City"
                      type="text"
                      labelName="City"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isCityFilled, touched.patient?.city && errors.patient?.city)}
                      isError={
                        touched.patient?.city && Boolean(errors.patient?.city)
                      }
                      errorMessage={errors.patient?.city}
                    />
                    <button
                      type="button"
                      onClick={() => setCityMode(false)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1"
                    >
                      Swap to Pincode
                    </button>
                    {showCitySuggestions && (
                      <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-md max-h-40 overflow-y-auto rounded-md mt-1">
                        {citySuggestions.map((item, idx) => (
                          <li
                            key={item.city + '-' + item.state + '-' + idx}
                            onClick={() => handleSelectCity(item)}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0 text-[13px]"
                          >
                            <span className="font-medium text-gray-800">{item.city}</span>
                            <span className="text-gray-500 ml-2">({item.state})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="col-span-12 md:col-span-6">
                {!ageMode ? (
                  <div className="relative w-full">
                    <Input
                      name="patient.dateofbirth"
                      type="date"
                      value={values?.patient?.dateofbirth}
                      placeholder="DD MM YYYY"
                      dateFormat="dd-MM-yyyy"
                      maxDate={new Date()}
                      clearIcon={null}
                      labelName="Date of Birth"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isDobFilled, touched.patient?.dateofbirth && errors.patient?.dateofbirth)}
                      onChange={(date) => {
                        setFieldValue("patient.dateofbirth", date);
                        setFieldValue("patient.age", "");
                      }}
                      isError={
                        touched.patient?.dateofbirth && Boolean(errors.patient?.dateofbirth)
                      }
                      errorMessage={errors.patient?.dateofbirth}
                    />
                    <button
                      type="button"
                      onClick={() => setAgeMode(true)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1"
                    >
                      Swap to Age
                    </button>
                  </div>
                ) : (
                  <div className="relative w-full">
                    <Input
                      name="patient.age"
                      value={values?.patient?.age}
                      onChange={(e) => {
                        setFieldValue("patient.age", e.target.value);
                        setFieldValue("patient.dateofbirth", "");
                      }}
                      placeholder="e.g. 54"
                      type="number"
                      labelName="Age"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isAgeFilled, touched.patient?.age && errors.patient?.age)}
                      isError={
                        touched.patient?.age && Boolean(errors.patient?.age)
                      }
                      errorMessage={errors.patient?.age}
                    />
                    <button
                      type="button"
                      onClick={() => setAgeMode(false)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1"
                    >
                      Swap to DoB
                    </button>
                  </div>
                )}
              </div>

              {/* Ergonomic Shifted Buttons (Add Report & Place Quick Order) */}
              {((isExistingPatient && values.patient.PID) || (role === "doctor" && onQuickOrder)) && (
                <div className="col-span-12 w-full flex gap-2 justify-end mt-2 pt-4 border-t border-gray-100">
                  {isExistingPatient && values.patient.PID && (
                    <button
                      type="button"
                      onClick={() => setShowReportOverlay(true)}
                      className="flex-1 sm:flex-initial text-center justify-center px-3.5 py-1.5 rounded-[6px] text-[11px] md:text-[12px] font-bold transition-all duration-300 bg-amber-50 text-amber-700 hover:bg-amber-600 hover:text-white border border-amber-200 shadow-sm flex items-center gap-1.5 group whitespace-nowrap"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform md:w-[14px] md:h-[14px]">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="12" y1="18" x2="12" y2="12"></line>
                        <line x1="9" y1="15" x2="15" y2="15"></line>
                      </svg>
                      <span className="hidden min-[420px]:inline">Add Clinical Report</span>
                      <span className="hidden max-[420px]:min-[340px]:inline">Add Report</span>
                      <span className="min-[340px]:hidden">+Report</span>
                    </button>
                  )}

                  {role === "doctor" && onQuickOrder && (
                    <button
                      type="button"
                      disabled={!isAllFilled}
                      onClick={onQuickOrder}
                      className={`flex-1 sm:flex-initial text-center justify-center px-3.5 py-1.5 rounded-[6px] text-[11px] md:text-[12px] font-bold transition-all duration-300 flex items-center gap-1.5 group whitespace-nowrap ${isAllFilled
                        ? "bg-orange-50 text-orange-700 hover:bg-orange-600 hover:text-white border border-orange-200 shadow-sm animate-pulse"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                        }`}
                      title={isAllFilled ? "Place a Quick Order" : "Fill all patient details first"}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform md:w-[14px] md:h-[14px]">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                      <span className="hidden min-[420px]:inline">Place Quick Order</span>
                      <span className="hidden max-[420px]:min-[340px]:inline">Quick Order</span>
                      <span className="min-[340px]:hidden">QO</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PatientOrderDetail);
